import { createFileRoute, Link } from "@tanstack/react-router";
import { Section } from "@/components/Section";
import { Reveal } from "@/components/Reveal";
import { Check, ArrowUpRight, Sparkles } from "lucide-react";
import { buildSeo, breadcrumbSchema } from "@/lib/seo";
import { JsonLd } from "@/components/JsonLd";
import { SITE } from "@/content/site";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { RangpurAreasStrip } from "@/components/RangpurAreasStrip";

export const Route = createFileRoute("/seo-training")({
  head: () => buildSeo({
    title: "SEO Training in Bangladesh | Freelancer Tamal",
    description: "Hands-on SEO training for founders, marketers, and in-house teams. Live cohorts, 1:1 coaching, and custom team workshops led by a working SEO consultant.",
    path: "/seo-training",
    ogEyebrow: "SEO Training",
    keywords: "SEO training Bangladesh, SEO course Rangpur, SEO coaching, SEO workshop, learn SEO",
  }),
  component: Training,
});

const TRACKS = [
  {
    t: "1:1 Coaching",
    p: "$300 / hour",
    d: "Bring your site, your stuck rankings, your team's questions. We work the actual problems live, on screen-share, with the same tools I use for clients.",
    pts: ["90-min sessions, recorded", "Pre-call audit so we don't waste time", "Slack follow-up for 7 days after", "AEO & schema deep-dives on request"],
  },
  {
    t: "Team Workshop",
    p: "From $4,500",
    d: "A two-day intensive for your in-house marketing or content team — fully customized to your stack, your industry, and your current SERP reality.",
    pts: ["Custom curriculum from your audit", "Up to 12 attendees, on-site or remote", "Templated playbooks (incl. AEO citation prompts)", "30-day post-workshop office hours"],
  },
  {
    t: "Founder Cohort",
    p: "$1,200 / seat",
    d: "A six-week live cohort for non-technical founders who want to stop being held hostage by their SEO agency. Small groups, weekly calls, real homework.",
    pts: ["6 live sessions, 90 min each", "Private community of operators", "Audit your own site by week 3 (incl. schema)", "Lifetime access to recordings"],
  },
];

function Training() {
  return (
    <>
      <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "SEO Training", path: "/seo-training" }]} />
      <Section headingLevel={1} eyebrow="SEO Training" title={<>Stop buying courses. <span className="text-gradient">Get coached.</span></>}>
        <p className="max-w-2xl text-lg text-muted-foreground -mt-8">
          Most SEO courses are recorded once and abandoned. Mine are live, small,
          and based on what's actually ranking in Google this quarter — not 2021.
        </p>
      </Section>

      <div className="container-x grid lg:grid-cols-3 gap-6 pb-24">
        {TRACKS.map((tr, i) => (
          <Reveal key={tr.t} delay={i * 0.08}>
            <div className="rounded-3xl border border-border bg-card p-10 h-full flex flex-col hover:border-primary/40 transition">
              <h2 className="font-display text-3xl">{tr.t}</h2>
              <div className="mt-2 text-gradient font-display text-2xl">{tr.p}</div>
              <p className="mt-4 text-muted-foreground">{tr.d}</p>
              <ul className="mt-6 space-y-3">
                {tr.pts.map((p) => (
                  <li key={p} className="flex items-start gap-3 text-sm">
                    <Check size={16} className="text-primary mt-1 flex-none" /> <span>{p}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="mt-8 inline-flex items-center gap-2 text-sm text-primary hover:underline">
                Apply for this track <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>
        ))}
      </div>

      <div className="container-x pb-24 -mt-12">
        <Reveal>
          <div className="rounded-2xl border border-border/70 bg-secondary/40 p-5 flex items-start gap-3">
            <Sparkles size={18} className="text-primary mt-0.5 flex-none" />
            <p className="text-sm text-muted-foreground">
              Every track now covers AEO (Answer Engine Optimization), AI citations,
              and schema markup as core modules — because that's where modern SEO is going.{" "}
              <Link
                to="/services/aeo-geo"
                className="text-foreground underline underline-offset-4 decoration-primary/60 hover:decoration-primary"
              >
                See the AEO &amp; GEO service →
              </Link>
            </p>
          </div>
        </Reveal>
      </div>

      <RangpurAreasStrip
        eyebrow="Training · Rangpur"
        title={<>In-person SEO training across <span className="text-gradient">Rangpur.</span></>}
        description="On-site workshops and 1:1 coaching available across every Rangpur neighborhood — from the Begum Rokeya University corridor to Medical Mor, Carmichael College Road, and beyond."
      />
      <JsonLd data={{
        "@context": "https://schema.org",
        "@type": "Course",
        name: "SEO Training in Bangladesh — 1:1 Coaching & Team Workshops",
        description: "Hands-on SEO, AEO and GEO training for founders, marketers, and in-house teams. Live cohorts, 1:1 coaching, and custom team workshops led by a working SEO consultant.",
        url: `${SITE.url}/seo-training`,
        inLanguage: "en",
        provider: { "@type": "Person", "@id": `${SITE.url}/#person`, name: SITE.fullName, url: `${SITE.url}/about` },
        hasCourseInstance: TRACKS.map((t) => ({
          "@type": "CourseInstance",
          name: t.t,
          description: t.d,
          courseMode: t.t === "Team Workshop" ? "Onsite" : "Online",
          courseWorkload: t.t === "Founder Cohort" ? "PT9H" : "PT1H30M",
          offers: { "@type": "Offer", price: t.p.replace(/[^0-9]/g, "") || "0", priceCurrency: "USD", category: t.p },
        })),
        offers: { "@type": "AggregateOffer", priceCurrency: "USD", lowPrice: "300", highPrice: "4500" },
      }} />
      <JsonLd data={breadcrumbSchema([{ name: "Home", path: "/" }, { name: "SEO Training", path: "/seo-training" }])} />
    </>
  );
}
